import React, {
  ComponentType,
  ReactNode,
  useState,
  useMemo,
  useEffect,
} from "react";
import _ from "lodash";
import { Link, useLocation, useParams } from "react-router-dom";
import { mutate } from "swr";
import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";

import { CARE_TEAM_EMAIL, PHONE } from "constants/at-need";
import { isInAppPath } from "helpers/links";
import { useGET, useGetIdToken, api, usePOST } from "hooks/useAPI";
import { useIsCareSpecialist } from "hooks/useIsCareSpecialist";
import { PortalContent } from "layouts/PortalLayout";
import KnowledgeBaseCallout from "components/KnowledgeBaseCallout";
import PageHeader from "components/PageHeader";
import ViewAsCustomerToggle from "components/ViewAsCustomerToggle";
import Loading from "components/Loading";
import {
  isPastArrangementWindow,
  isArrangementLocked,
  canAccessArrangementFlow,
  getArrangementStatus,
  hasReachedStage,
  getStageDisplayDetails,
} from "helpers/at-need";
import { caseAccessFor, type MinimalCase } from "helpers/me-cases";
import { quoteIdForCase, quotePathById } from "helpers/me-home";
import { useMeHome } from "hooks/useMeHome";
import AppButton from "components/Button";
import { useFirebase } from "firebase";
import NotFound from "components/NotFound/NotFound";
import CaseProgressList, {
  ProgressStep,
  CaseProgressListSkeleton,
} from "components/CaseProgressList";
import { useUser } from "context/UserContext";
import { formatPhoneNumber, formatPhoneNumberForAPI } from "helpers/phone";
import { pluralize } from "helpers/grammar";
import { ClockIcon, EnvelopeIcon, PhoneIcon } from "@heroicons/react/20/solid";
import { Button } from "react-aria-components";
import LeadCaseLanding from "./LeadCaseLanding";
import { leadCaseLandingFor } from "./lead-landing";
import StartHub from "./StartHub";
import { useStartHubState } from "./useStartHubState";

// Past these stages, vitals is treated as complete even without a
// submission record (legacy cases) and the arrangement window has closed.
const EMPTY_APP_DATA: { properties: Record<string, any> } = { properties: {} };
const CARE_SPECIALIST_ASSIGNMENT_MESSAGE =
  "Your dedicated care specialist will be assigned to you on the next business day.";

// Generic ActionButton component for consistent styling across case progress stages
interface ActionButtonProps {
  variant?: "primary" | "secondary";
  isLoading?: boolean;
  isDisabled?: boolean;
  href?: string;
  target?: string;
  rel?: string;
  onPress?: () => void;
  children: React.ReactNode;
  className?: string;
  trackingEvent?: string;
  trackingProperties?: Record<string, any>;
}

const ActionButton = ({
  variant = "primary",
  isLoading = false,
  isDisabled = false,
  href,
  target,
  rel,
  onPress,
  children,
  className = "",
  trackingEvent,
  trackingProperties,
}: ActionButtonProps) => {
  const posthog = usePostHog();
  const baseStyles = "rounded-lg px-6 py-4 font-bold transition-colors";
  const variantStyles = {
    primary:
      "bg-cantelope text-soil hover:bg-cantelope/80 border-2 border-transparent",
    secondary:
      "border-2 border-cantelope bg-white text-soil hover:bg-cantelope/10",
  };
  const disabledStyles = "disabled:opacity-50 disabled:cursor-not-allowed";

  const buttonClasses = `${baseStyles} ${variantStyles[variant]} ${disabledStyles} ${className}`;

  const handleClick = () => {
    if (trackingEvent) {
      posthog?.capture(trackingEvent, trackingProperties);
    }
    onPress?.();
  };

  const handleLinkClick = () => {
    if (trackingEvent) {
      posthog?.capture(trackingEvent, trackingProperties);
    }
  };

  if (href) {
    const linkClassName = `inline-block text-center ${buttonClasses} ${isDisabled || isLoading ? "pointer-events-none opacity-50" : ""}`;
    const content = isLoading ? "Processing..." : children;
    if (isInAppPath(href, target)) {
      return (
        <Link to={href} onClick={handleLinkClick} className={linkClassName}>
          {content}
        </Link>
      );
    }
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        onClick={handleLinkClick}
        className={linkClassName}
      >
        {content}
      </a>
    );
  }

  return (
    <Button
      onPress={handleClick}
      isDisabled={isDisabled || isLoading}
      className={buttonClasses}
    >
      {isLoading ? "Processing..." : children}
    </Button>
  );
};

interface CareTeamContactFallbackProps {
  contactPrompt: ReactNode;
}

const CareTeamContactFallback = ({
  contactPrompt,
}: CareTeamContactFallbackProps) => (
  <>
    {CARE_SPECIALIST_ASSIGNMENT_MESSAGE} {contactPrompt}{" "}
    <a href={`mailto:${CARE_TEAM_EMAIL}`} className="text-evergreen underline">
      {CARE_TEAM_EMAIL}
    </a>{" "}
    or call{" "}
    <a
      href={`tel:${formatPhoneNumberForAPI(PHONE)}`}
      className="text-evergreen underline"
    >
      {formatPhoneNumber(PHONE)}
    </a>
    .
  </>
);

// Skeleton loader components
const NextActionCardSkeleton = () => (
  <div className="animate-pulse rounded-xl border border-oat-200 bg-white p-6 shadow-sm">
    <div className="mb-2 h-4 w-24 rounded bg-gray-200"></div>
    <div className="mb-4 h-8 w-3/4 rounded bg-gray-200"></div>
    <div className="space-y-2">
      <div className="h-4 w-full rounded bg-gray-200"></div>
      <div className="h-4 w-5/6 rounded bg-gray-200"></div>
    </div>
    <div className="mt-4 h-12 w-32 rounded bg-gray-200"></div>
  </div>
);

interface CaseDetails {
  record_id: string;
  dcd_full_name?: string;
  stage?: string;
  status?: string;
  care_specialist?: string;
  date_entered_stage?: string;
  labels?: string[];
  [key: string]: any;
}

interface TeamMember {
  record_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  photo?: {
    file_uri?: string;
    file_name?: string;
  };
}

interface CareSpecialistCardProps {
  careSpecialist: TeamMember | undefined;
  isLoading: boolean;
  error: any;
}

interface NextActionCardProps {
  caseData: CaseDetails | undefined;
  currentStage: CaseProgressStage | null;
  currentStageContext?: Record<string, any> & {
    appData: {
      properties?: Record<string, any>;
    };
  };
  careSpecialist: TeamMember | undefined;
}

const NextActionCard = ({
  caseData,
  currentStage,
  currentStageContext,
  careSpecialist,
}: NextActionCardProps) => {
  if (!caseData || !currentStage) {
    // All stages are complete or no data
    return (
      <div className="rounded-xl border border-oat-200 bg-white p-6 shadow-sm">
        <div className="mb-2 text-sm font-semibold uppercase tracking-wide text-evergreen">
          Current Status
        </div>
        <h3 className="mb-0 font-serif text-2xl font-bold text-soil">
          All Steps Complete
        </h3>
        <div className="text-base">
          All arrangement steps have been completed.
        </div>
      </div>
    );
  }

  // Determine if we're in a waiting state (stages that don't require user action)
  const isWaiting = !currentStage.hasUserAction;

  return (
    <div className="rounded-xl border border-oat-200 bg-white p-6 shadow-sm">
      <div className="mb-2 text-sm font-semibold uppercase tracking-wide text-evergreen">
        {isWaiting ? "Current Status" : "Your Next Step"}
      </div>
      <h3 className="mb-0 font-serif text-2xl font-bold text-soil">
        {currentStage.name}
      </h3>
      {isWaiting && (
        <div className="text-base">
          There is no action required from you at this time.
        </div>
      )}
      {currentStage.timeToComplete && (
        <div className="mb-2 flex items-center gap-1 text-base text-oat-700">
          <ClockIcon className="h-4 w-4" aria-hidden="true" />
          <span className="relative -top-px">
            {currentStage.timeToComplete}
          </span>
        </div>
      )}
      {currentStage.component && currentStageContext && (
        <currentStage.component
          caseData={caseData}
          context={currentStageContext}
          status="current"
          careSpecialist={careSpecialist}
        />
      )}
    </div>
  );
};

function CareSpecialistCard({
  careSpecialist,
  isLoading,
  error,
}: CareSpecialistCardProps) {
  return (
    <div className="rounded-xl border border-oat-200 bg-white p-6 shadow-sm">
      <h4 className="mb-4 text-xs font-bold uppercase tracking-[.14em] text-evergreen">
        Your Care Specialist
      </h4>
      <CareSpecialistCardBody
        careSpecialist={careSpecialist}
        isLoading={isLoading}
        error={error}
      />
    </div>
  );
}

const CARE_SPECIALIST_ACTION_CLASSES =
  "inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-soil px-4 py-2.5 text-sm font-bold text-soil transition-colors hover:bg-soil hover:text-oat";

function CareSpecialistCardBody({
  careSpecialist,
  isLoading,
  error,
}: CareSpecialistCardProps) {
  const posthog = usePostHog();

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="mb-2 h-6 w-40 rounded bg-oat-100"></div>
        <div className="mb-5 h-4 w-48 rounded bg-oat-100"></div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="h-10 flex-1 rounded-md bg-oat-100"></div>
          <div className="h-10 flex-1 rounded-md bg-oat-100"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-soil">
        Could not load care specialist details.
      </p>
    );
  }

  if (!careSpecialist) {
    return (
      <div className="min-h-16">
        <p className="text-sm text-soil">
          {CARE_SPECIALIST_ASSIGNMENT_MESSAGE}
        </p>
      </div>
    );
  }

  const fullName =
    [careSpecialist.first_name, careSpecialist.last_name]
      .filter(Boolean)
      .join(" ") || "Care Specialist";

  return (
    <>
      <p className="font-serif text-xl leading-snug text-soil">{fullName}</p>
      <p className="mt-0.5 text-sm text-oat-700">
        Care specialist · Your personal point of contact
      </p>
      {(careSpecialist.phone || careSpecialist.email) && (
        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          {careSpecialist.phone && (
            <a
              href={`tel:${formatPhoneNumberForAPI(careSpecialist.phone)}`}
              onClick={() =>
                posthog?.capture("basecamp_case_details_contact_phone", {
                  care_specialist_phone: careSpecialist.phone,
                })
              }
              className={CARE_SPECIALIST_ACTION_CLASSES}
            >
              <PhoneIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
              {formatPhoneNumber(careSpecialist.phone)}
            </a>
          )}
          {careSpecialist.email && (
            <a
              href={`mailto:${careSpecialist.email}`}
              onClick={() =>
                posthog?.capture("basecamp_case_details_contact_email", {
                  care_specialist_email: careSpecialist.email,
                })
              }
              title={careSpecialist.email}
              className={CARE_SPECIALIST_ACTION_CLASSES}
            >
              <EnvelopeIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
              {careSpecialist.first_name
                ? `Email ${careSpecialist.first_name}`
                : "Email"}
            </a>
          )}
        </div>
      )}
    </>
  );
}

interface CaseProgressComponentProps {
  caseData: any;
  status: "current" | "completed" | "upcoming";
  context: Record<string, any> & {
    appData: {
      properties?: Record<string, any>;
    };
  };
  careSpecialist?: TeamMember;
}

function CaseProgressNOK({
  caseData,
  careSpecialist,
}: CaseProgressComponentProps) {
  const {
    data: apiResponse,
    isLoading: isLoadingCase,
    error: caseError,
  } = useGET(caseData ? `/cases/${caseData.record_id}/contacts` : null);

  if (isLoadingCase) {
    return <div>Loading...</div>;
  }

  if (caseError) {
    return (
      <div>
        We're having trouble loading your case details. Please try again later.
      </div>
    );
  }

  const nextOfKinContacts =
    apiResponse?.results?.filter((contact: any) =>
      contact.role?.includes("Next of Kin"),
    ) || [];

  return (
    <div className="flex flex-col gap-4">
      {nextOfKinContacts.length > 0 ? (
        <>
          <div>Our records show the following next of kin:</div>
          {nextOfKinContacts.map((contact: any) => (
            <div className="flex flex-col gap-1" key={contact.record_id}>
              <span className="text-xs text-evergreen">{contact.relation}</span>
              <span className="text-evergreen">
                {contact.full_name}
                {contact.email && (
                  <>
                    <br />
                    {contact.email}
                  </>
                )}
              </span>
            </div>
          ))}
          {careSpecialist ? (
            <div>
              If you need to update this information, please email your Care
              Specialist at{" "}
              <a
                href={`mailto:${careSpecialist.email}`}
                className="text-evergreen hover:underline"
              >
                {careSpecialist.email}
              </a>{" "}
              or call{" "}
              <a
                href={`tel:${formatPhoneNumberForAPI(careSpecialist.phone || PHONE)}`}
                className="text-evergreen hover:underline"
              >
                {formatPhoneNumber(careSpecialist.phone || PHONE)}
              </a>
              .
            </div>
          ) : (
            <div>
              <CareTeamContactFallback contactPrompt="If you need to update this information before then, please reach out to our care team at" />
            </div>
          )}
        </>
      ) : (
        <>
          <div>
            We don't currently have next of kin contact information on file.
          </div>
          {careSpecialist ? (
            <div>
              This information is required to complete the cremation. Please
              reach out to your Care Specialist at{" "}
              <a
                href={`mailto:${careSpecialist.email}`}
                className="text-evergreen hover:underline"
              >
                {careSpecialist.email}
              </a>{" "}
              or call{" "}
              <a
                href={`tel:${formatPhoneNumberForAPI(careSpecialist.phone || PHONE)}`}
                className="text-evergreen hover:underline"
              >
                {formatPhoneNumber(careSpecialist.phone || PHONE)}
              </a>{" "}
              to provide this information.
            </div>
          ) : (
            <div>
              <CareTeamContactFallback contactPrompt="To provide this information before then, please reach out to our care team at" />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CaseProgressArrangement({
  caseData,
  status,
}: CaseProgressComponentProps) {
  const arrangeHref = caseData?.record_id
    ? `/cases/${caseData.record_id}/arrange`
    : "/";

  if (status === "current") {
    return (
      <div className="flex flex-col gap-4">
        <p>
          Review cremation packages, urns, and services to confirm the details
          of your arrangement.
        </p>
        <ActionButton
          href={arrangeHref}
          className="sm:self-start"
          trackingEvent="basecamp_case_details_action_arrangement_click"
          trackingProperties={{
            case_id: caseData?.record_id,
            stage: "arrangement",
          }}
        >
          Complete Arrangement
        </ActionButton>
      </div>
    );
  }

  return (
    <div>
      Your arrangement selections are complete. We're using them to prepare your
      documents and order.
    </div>
  );
}

function CaseProgressVitals({
  caseData,
  status,
  context,
}: CaseProgressComponentProps) {
  if (status === "current") {
    return (
      <div className="flex flex-col gap-4">
        <p>
          We'll use this information to complete the required paperwork for
          permits and death certificates.
        </p>
        <p>
          If you don't have every detail right now, you can save your progress
          and come back.
        </p>
        <ActionButton
          href={caseData?.vitals_url}
          className="sm:self-start"
          trackingEvent="basecamp_case_details_action_vitals_form_click"
          trackingProperties={{ case_id: caseData?.record_id, stage: "vitals" }}
        >
          Complete Vitals Form
        </ActionButton>
      </div>
    );
  }

  if (context?.vitals_submissions?.length > 0) {
    return (
      <div>
        Vitals forms have been completed and submitted. We're using this
        information to prepare the necessary permits and death certificates.
      </div>
    );
  }

  return <div>Completed</div>;
}

function CaseProgressDeathCertificates({
  caseData,
  status,
  careSpecialist,
}: CaseProgressComponentProps) {
  if (status === "completed") {
    const quantity = caseData?.number_of_death_certificates;

    if (quantity === 0) {
      return (
        <div className="flex flex-col gap-2">
          <div>You chose not to order death certificates through After.</div>
          <div>
            You can still order death certificates yourself at any time.
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        <div>We've ordered the death certificates for your loved one.</div>
        <div>
          They typically arrive by mail within 3 to 4 weeks after cremation.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <p>
        We have all the information we need to order the death certificates
        you've requested.
      </p>
      {careSpecialist ? (
        <p>
          If you'd like to order additional death certificates, please reach out
          to your Care Specialist, {careSpecialist.first_name}, at{" "}
          <a
            href={`mailto:${careSpecialist.email}`}
            className="text-evergreen underline"
          >
            {careSpecialist.email}
          </a>{" "}
          or call{" "}
          <a
            href={`tel:${formatPhoneNumberForAPI(careSpecialist.phone || PHONE)}`}
            className="text-evergreen underline"
          >
            {formatPhoneNumber(careSpecialist.phone || PHONE)}
          </a>
          .
        </p>
      ) : (
        <p>
          <CareTeamContactFallback contactPrompt="If you'd like to order additional death certificates before then, please reach out to our care team at" />
        </p>
      )}
    </div>
  );
}

function CaseProgressReviewAndPreparation(_props: CaseProgressComponentProps) {
  return (
    <div className="flex flex-col gap-2">
      <p>The crematory has received the necessary permits and documents.</p>
      <p>
        Throughout this process, our professional team treats your loved one
        with the same respect and dignity we would want for our own family
        members, providing you with the certainty that your loved one's final
        care reflects the love and honor they deserve.
      </p>
      <p>
        We assign a unique identification number and place a permanent metal
        disc with your loved one that stays with them throughout cremation and
        remains with their cremated remains forever.
      </p>
      <p>
        Our certified operators follow strict protocols in secure,
        climate-controlled facilities, maintaining detailed documentation that
        creates an unbroken chain of care from the moment we receive your loved
        one until we return their cremated remains to you.
      </p>
    </div>
  );
}

function CaseProgressObituary({
  caseData,
  status,
  context,
}: CaseProgressComponentProps) {
  const posthog = usePostHog();
  const { trigger: passOnObituary, isMutating } = usePOST(
    `/cases/${caseData?.record_id}/app-data/actions/skip-obituary`,
    {
      onSuccess: () => {
        mutate(`/cases/${caseData?.record_id}/app-data`);
      },
    },
  );

  const handlePassOnObituary = async () => {
    try {
      await passOnObituary();
    } catch (error) {
      console.error("Failed to pass on obituary:", error);
    }
  };

  if (status === "current") {
    return (
      <div className="flex flex-col gap-4">
        <p>
          In these moments of remembrance, crafting an obituary for your loved
          one can be a meaningful way to honor their memory and reflect on their
          life.
        </p>
        <p>
          We also encourage you to invite friends and family to contribute their
          fond memories, creating a rich tapestry of shared experiences and love
          through our "A Living Memory" section of the obituary.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row">
          <ActionButton
            href={caseData?.obit_url}
            target="_blank"
            trackingEvent="basecamp_case_details_external_link_obituary_create"
            trackingProperties={{
              case_id: caseData?.record_id,
              stage: "obituary",
            }}
          >
            Create Obituary
          </ActionButton>
          <ActionButton
            variant="secondary"
            onPress={handlePassOnObituary}
            isLoading={isMutating}
            trackingEvent="basecamp_case_details_action_obituary_skip"
            trackingProperties={{
              case_id: caseData?.record_id,
              stage: "obituary",
            }}
          >
            Skip for Now
          </ActionButton>
        </div>
        <p className="text-sm text-soil/80">
          Review our{" "}
          <a
            href="https://www.after.com/articles/how-to-write-an-obituary-with-free-templates"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              posthog?.capture(
                "basecamp_case_details_external_link_obituary_guide",
                { case_id: caseData?.record_id, stage: "obituary" },
              )
            }
            className="font-semibold underline"
          >
            Obituary Writing Guide
          </a>{" "}
          for helpful tips and examples.
        </p>
      </div>
    );
  }

  if (context.appData.properties?.obituary_passed) {
    return (
      <div className="flex flex-col gap-2">
        <div>You chose to skip creating an obituary.</div>
        <div>
          You're welcome to come back and{" "}
          <a
            className="underline"
            href={caseData?.obit_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            create an obituary
          </a>{" "}
          at any time.
        </div>
      </div>
    );
  }

  if (context?.obituary_submissions?.length > 0) {
    return <div>Thank you for submitting your obituary.</div>;
  }

  return <div>Obituary completed</div>;
}

function CaseProgressCremationCompleted({
  caseData,
  status,
  context,
}: CaseProgressComponentProps) {
  // Check if we have detailed delivery information
  const hasDeliveryRecords = context?.delivery_records?.length > 0;

  if (status === "current") {
    return (
      <div className="flex flex-col gap-2">
        <p>
          We are preparing to complete your loved one's honorable cremation.
          There is no action required from you at this time.
        </p>
        <p>
          Your care specialist will notify you when your loved one's cremation
          is complete to coordinate the safe return of their remains.
        </p>
      </div>
    );
  }

  // If we have delivery records, show a brief acknowledgment
  if (hasDeliveryRecords) {
    return (
      <div className="flex flex-col gap-2">
        <p>
          Your loved one's cremation has been completed with the utmost care and
          respect.
        </p>
      </div>
    );
  }

  // If stage is Delivery but no delivery records yet, provide more context
  if (caseData?.stage === "Delivery") {
    return (
      <div className="flex flex-col gap-2">
        <p>
          Your loved one's cremation has been completed with the utmost care and
          respect.
        </p>
        <p>
          Following cremation, there is a brief processing period as we
          carefully prepare and place the cremated remains in the urn you've
          selected. Your Care Specialist will be reaching out soon to coordinate
          the safe return of your loved one.
        </p>
      </div>
    );
  }

  // Default message for completed state
  return (
    <div className="flex flex-col gap-2">
      <p>
        Your loved one's cremation has been completed with the utmost care and
        respect.
      </p>
    </div>
  );
}

function CaseProgressDelivery({
  caseData,
  status,
  context,
}: CaseProgressComponentProps) {
  const posthog = usePostHog();
  if (status !== "completed" && context?.delivery_records?.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        <p>
          We're preparing to deliver your loved one's remains to the designated
          recipient.
        </p>
        <p className="text-sm text-gray-600">
          Delivery to: {caseData?.delivery_address || "Address pending"}
        </p>
      </div>
    );
  }

  if (context?.delivery_records?.length > 0) {
    return (
      <div className="flex flex-col gap-4">
        {context?.delivery_records.map((record: any) => (
          <div
            key={record.record_id}
            className="rounded-lg border border-oat-200 p-4"
          >
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-evergreen">
                  {record.delivery_method_type === "CERTIFIED_MAIL"
                    ? "Certified Mail Delivery"
                    : "Delivery"}
                </span>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-medium ${
                    record.completed
                      ? "bg-green-100 text-green-800"
                      : record.status === "IN_TRANSIT"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {record.completed
                    ? "Delivered"
                    : record.status === "IN_TRANSIT"
                      ? "In Transit"
                      : "Preparing"}
                </span>
              </div>

              <div className="text-sm text-gray-600">
                <p>
                  <strong>Recipient:</strong> {record.recipient_name}
                </p>
                <p>
                  <strong>Address:</strong> {record.destination_address}
                </p>
                {record.recipient_phone && (
                  <p>
                    <strong>Phone:</strong>{" "}
                    {formatPhoneNumber(record.recipient_phone)}
                  </p>
                )}
              </div>

              {record.tracking_number && (
                <div className="mt-2 text-sm">
                  <strong>Tracking Number:</strong>{" "}
                  <a
                    href={`https://tools.usps.com/go/TrackConfirmAction?tLabels=${record.tracking_number}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      posthog?.capture(
                        "basecamp_case_details_external_link_tracking",
                        {
                          case_id: caseData?.record_id,
                          stage: "delivery",
                          tracking_number: record.tracking_number,
                          delivery_method: record.delivery_method_type,
                        },
                      )
                    }
                  >
                    <span className="font-mono">{record.tracking_number}</span>
                  </a>
                </div>
              )}

              {record.special_instructions &&
                record.special_instructions !== "None" && (
                  <div className="mt-2 text-sm text-gray-600">
                    <strong>Special Instructions:</strong>{" "}
                    {record.special_instructions}
                  </div>
                )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return <div>Delivery information will be available soon.</div>;
}

function CaseProgressSignDocuments({
  caseData: _caseData,
  status,
  context,
}: CaseProgressComponentProps) {
  const { user } = useUser();
  const userEmail = user?.email?.toLowerCase();

  const createSignatureList = (recipients: CaseDocumentRecipient[]) => (
    <div className="my-4 space-y-4">
      {recipients.map((recipient) => (
        <div key={recipient.id} className="flex items-center gap-3">
          {recipient.has_completed ? (
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="1.5"
                y="1.5"
                width="17"
                height="17"
                rx="8.5"
                fill="white"
              />
              <rect
                x="1.5"
                y="1.5"
                width="17"
                height="17"
                rx="8.5"
                stroke="#FFB885"
                strokeWidth="3"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10 20C12.6522 20 15.1957 18.9464 17.0711 17.0711C18.9464 15.1957 20 12.6522 20 10C20 7.34784 18.9464 4.8043 17.0711 2.92893C15.1957 1.05357 12.6522 0 10 0C7.34784 0 4.8043 1.05357 2.92893 2.92893C1.05357 4.8043 0 7.34784 0 10C0 12.6522 1.05357 15.1957 2.92893 17.0711C4.8043 18.9464 7.34784 20 10 20ZM14.6337 8.38375C14.8614 8.148 14.9874 7.83224 14.9846 7.5045C14.9817 7.17675 14.8503 6.86324 14.6185 6.63148C14.3868 6.39972 14.0732 6.26826 13.7455 6.26541C13.4178 6.26256 13.102 6.38855 12.8663 6.61625L8.75 10.7325L7.13375 9.11625C6.898 8.88855 6.58224 8.76256 6.2545 8.76541C5.92675 8.76826 5.61324 8.89972 5.38148 9.13148C5.14972 9.36324 5.01826 9.67675 5.01541 10.0045C5.01256 10.3322 5.13855 10.648 5.36625 10.8837L7.86625 13.3837C8.10066 13.6181 8.41854 13.7497 8.75 13.7497C9.08146 13.7497 9.39934 13.6181 9.63375 13.3837L14.6337 8.38375Z"
                fill="#91B89E"
              />
            </svg>
          ) : (
            <div className="h-5 w-5 rounded-full bg-oat"></div>
          )}
          {recipient.role === "funeral-home" ? (
            <div className="flex flex-col text-sm">
              <span className="font-bold">After's Funeral Director</span>
              {!recipient.has_completed && (
                <div className="text-sm text-evergreen">In Review</div>
              )}
            </div>
          ) : (
            <div className="flex flex-col text-sm">
              <span className="font-bold">
                {recipient.first_name} {recipient.last_name}
              </span>
              <span className="text-evergreen">{recipient.email}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  // Filter for cremation authorization documents
  const cremationAuthDocs =
    context?.case_documents?.filter((doc: any) =>
      doc.packet_slug?.startsWith("cremation_authorization"),
    ) || [];

  if (status !== "completed" && cremationAuthDocs.length > 0) {
    // Show the most recently created cremation authorization document
    const doc = _.orderBy(cremationAuthDocs, "created_at", "desc")[0];

    if (doc.recipients && doc.recipients.length > 0) {
      // Count signatures
      const totalSigners = doc.recipients.length;
      const completedSignatures = doc.recipients.filter(
        (recipient: CaseDocumentRecipient) => recipient.has_completed,
      ).length;
      const remainingSignatures = totalSigners - completedSignatures;

      // Check if current user has signed and get their signing URL
      const currentUserRecipient = doc.recipients.find(
        (recipient: CaseDocumentRecipient) =>
          recipient.email?.toLowerCase() === userEmail?.toLowerCase(),
      );
      const userHasSigned = currentUserRecipient?.has_completed || false;
      const url = currentUserRecipient?.shared_link || "";

      return (
        <div className="flex flex-col gap-2">
          <div>
            We are waiting on{" "}
            <span className="inline-block underline decoration-dashed underline-offset-4">
              {`${remainingSignatures} of ${totalSigners} signatures`}
            </span>
            .
          </div>
          <div className="">
            To continue scheduling{" "}
            {_caseData?.dcd_full_name || "your loved one"}'s cremation, please
            remind the people who haven't signed below to check their email for
            their personal signing link.
          </div>
          {createSignatureList(doc.recipients)}

          {userHasSigned || !url ? (
            <div className="rounded-lg border border-sage/20 bg-sage/10 px-4 py-3">
              <div className="mb-1 flex items-center gap-2">
                <span className="text-base font-bold text-evergreen">
                  You've completed your part
                </span>
              </div>
              <p className="text-sm text-soil">
                We'll notify you as soon as all required signatures are
                collected.
              </p>
            </div>
          ) : (
            <ActionButton
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="sm:self-start"
              trackingEvent="basecamp_case_details_action_sign_documents"
              trackingProperties={{
                case_id: _caseData?.record_id,
                stage: "sign_documents",
                document_type: "cremation_authorization",
              }}
            >
              Sign Cremation Authorization
            </ActionButton>
          )}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-4">
        <p>
          Please review and sign the cremation authorization documents to
          proceed with the arrangement.
        </p>
      </div>
    );
  }

  // Check if all cremation auth documents are complete
  const allDocsComplete =
    cremationAuthDocs.length > 0 &&
    cremationAuthDocs.every((doc: CaseDocument) => {
      const docStatus = doc.status?.toLowerCase();
      return docStatus === "complete";
    });

  if (status === "completed" || allDocsComplete) {
    return (
      <div>
        All required signatures have been collected. We're now coordinating with
        the crematory to schedule the service.
      </div>
    );
  }

  return (
    <div>
      Your Care Specialist will be in touch shortly to coordinate signing the
      required documents.
    </div>
  );
}

function CaseProgressConfirmDeathCertificates({
  caseData,
  status,
  careSpecialist,
}: CaseProgressComponentProps) {
  const { trigger: confirmAmount, isMutating } = usePOST(
    `/cases/${caseData?.record_id}/app-data/actions/confirm-death-certificate-amount`,
    {
      onSuccess: () => {
        mutate(`/cases/${caseData?.record_id}/app-data`);
      },
    },
  );

  const handleConfirmAmount = async () => {
    try {
      await confirmAmount();
    } catch (error) {
      console.error("Failed to confirm death certificate amount:", error);
    }
  };

  const quantity = caseData?.number_of_death_certificates || 0;

  if (status === "completed") {
    return (
      <div className="flex flex-col gap-2">
        <div>
          {quantity === 0 ? (
            <>You chose not to order any death certificates.</>
          ) : (
            <>
              You have requested{" "}
              <span className="font-bold">
                {`${quantity} death ${pluralize(quantity, "certificate", "certificates")}`}
              </span>
              .
            </>
          )}
        </div>
      </div>
    );
  }

  if (status === "current") {
    return (
      <div className="flex flex-col gap-4">
        <div>
          {quantity === 0 ? (
            <>
              You haven't requested any death certificates. Death certificates
              are often needed for settling affairs like insurance claims, bank
              accounts, and other legal matters.
            </>
          ) : (
            <>
              You have requested{" "}
              <span className="font-bold">
                {`${quantity} death ${pluralize(quantity, "certificate", "certificates")}`}
              </span>{" "}
              which we will order on your behalf after all the required
              documents are signed.
            </>
          )}
        </div>
        {careSpecialist ? (
          <div>
            {quantity === 0
              ? "If you'd like to order death certificates,"
              : "To change this quantity,"}
            email your Care Specialist at{" "}
            <a
              href={`mailto:${careSpecialist.email}`}
              className="text-evergreen underline"
            >
              {careSpecialist.email}
            </a>{" "}
            or call{" "}
            <a
              href={`tel:${formatPhoneNumberForAPI(careSpecialist.phone || PHONE)}`}
              className="text-evergreen underline"
            >
              {formatPhoneNumber(careSpecialist.phone || PHONE)}
            </a>
            .
          </div>
        ) : (
          <div>
            <CareTeamContactFallback
              contactPrompt={
                quantity === 0
                  ? "If you'd like to order death certificates before then, please reach out to our care team at"
                  : "To change this quantity before then, please reach out to our care team at"
              }
            />
          </div>
        )}
        <div className="flex flex-col gap-3 sm:flex-row">
          <ActionButton
            onPress={handleConfirmAmount}
            isLoading={isMutating}
            trackingEvent="basecamp_case_details_action_death_certificates_confirm"
            trackingProperties={{
              case_id: caseData?.record_id,
              stage: "confirm_death_certificates",
              quantity,
            }}
          >
            {quantity === 0
              ? "Continue without death certificates"
              : `Continue with ${quantity} death ${pluralize(quantity, "certificate", "certificates")}`}
          </ActionButton>
          {careSpecialist && (
            <ActionButton
              variant="secondary"
              href={`mailto:${careSpecialist.email}`}
              target="_blank"
              rel="noopener noreferrer"
              trackingEvent="basecamp_case_details_contact_email_death_certificates"
              trackingProperties={{
                case_id: caseData?.record_id,
                stage: "confirm_death_certificates",
                care_specialist_email: careSpecialist.email,
              }}
            >
              Email {careSpecialist.first_name}
            </ActionButton>
          )}
        </div>
      </div>
    );
  }

  return <div>Confirm Death Certificates</div>;
}

interface CaseDocumentRecipient {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  has_completed: boolean;
  role: string;
  shared_link?: string; // Signing URL for the recipient
}

interface CaseDocument {
  packet_name: string;
  packet_slug: string;
  created_at: string;
  status: string;
  recipients: CaseDocumentRecipient[];
}

interface CaseDocumentsResponse {
  results: CaseDocument[];
}

type ApiFunction = (url: string, options?: any) => Promise<any>;

type GetContextFunction = (
  caseData: any,
  api: ApiFunction,
) => Promise<Record<string, any>>;

type GetCompletedDateFunction = (
  caseData: any,
  context: Record<string, any>,
) => Date | undefined;

type IsCompletedFunction = (
  caseData: any,
  context: Record<string, any>,
) => boolean;

type IsVisibleFunction = (caseData: any) => boolean;

type CaseProgressStage = {
  slug: string;
  name: string;
  hasUserAction: boolean;
  timeToComplete?: string;
  component?: ComponentType<CaseProgressComponentProps>;
  getContext?: GetContextFunction;
  getCompletedDate?: GetCompletedDateFunction;
  isCompleted: IsCompletedFunction;
  isVisible?: IsVisibleFunction;
};

const CASE_PROGRESS_STAGES: CaseProgressStage[] = [
  {
    slug: "start",
    name: "Next of Kin",
    hasUserAction: false,
    component: CaseProgressNOK,
    getCompletedDate: (caseData: any) => {
      // This stage is always considered completed, could use case creation date
      return caseData?.date_entered_stage
        ? new Date(caseData.date_entered_stage)
        : undefined;
    },
    isCompleted: () => {
      return true;
    },
  },
  {
    slug: "arrangement",
    name: "Complete Your Arrangement",
    hasUserAction: true,
    timeToComplete: "About 15 minutes to complete",
    component: CaseProgressArrangement,
    getContext: async (caseData, api) => {
      try {
        const appData = await api(`/cases/${caseData.record_id}/app-data`);
        const status = getArrangementStatus(appData);
        return { arrangement_status: status, arrangement_data_loaded: true };
      } catch (error: any) {
        if (error?.statusCode === 404) {
          // No app-data document means no arrangement has been started.
          return { arrangement_status: null, arrangement_data_loaded: true };
        }
        // Surface other errors to the outer try/catch so the context falls
        // back to {} and we don't silently treat a transient failure as
        // "no record exists".
        throw error;
      }
    },
    getCompletedDate: (_caseData, context) => {
      const updatedAt = context?.arrangement_updated_at;
      return updatedAt ? new Date(updatedAt) : undefined;
    },
    isCompleted: (caseData, context) => {
      const status = context?.arrangement_status ?? null;
      if (isArrangementLocked(status)) return true;
      // Hide the CTA only once we've confirmed the family cannot (re-)enter
      // the flow — the exact condition the route guard uses, so the timeline
      // and the arrangements page can never disagree. Require
      // `arrangement_data_loaded` so a transient app-data fetch failure
      // (which falls back to {}) doesn't wrongly hide the CTA.
      if (
        context?.arrangement_data_loaded &&
        !canAccessArrangementFlow(caseData?.stage, status)
      ) {
        return true;
      }
      return false;
    },
  },
  {
    slug: "vitals",
    name: "Complete Vitals Form",
    hasUserAction: true,
    timeToComplete: "About 15 minutes to complete",
    component: CaseProgressVitals,
    getContext: async (caseData, api) => {
      try {
        const vitalsSubmissions = await api(
          `/cases/${caseData.record_id}/vitals/submissions`,
        );
        return {
          vitals_submissions: vitalsSubmissions.results || [],
        };
      } catch (error: any) {
        if (error?.statusCode === 404) {
          // 404 is expected when no submissions exist yet
          return {
            vitals_submissions: [],
          };
        }
        throw error; // Re-throw other errors
      }
    },
    getCompletedDate: (caseData, context) => {
      const { vitals_submissions } = context;

      if (!vitals_submissions || vitals_submissions.length === 0) {
        return undefined;
      }

      const firstSubmission = _.orderBy(
        vitals_submissions,
        "created_at",
        "asc",
      )[0];

      return firstSubmission?.created_at
        ? new Date(firstSubmission.created_at)
        : undefined;
    },
    isCompleted: (caseData, context) => {
      if (!caseData) return false;

      const { vitals_submissions } = context;

      if (!vitals_submissions || vitals_submissions.length === 0) {
        return isPastArrangementWindow(caseData.stage);
      }

      return true;
    },
  },
  {
    slug: "confirm_death_certificates",
    name: "Confirm Death Certificates",
    hasUserAction: true,
    // The arrangement flow already asks who orders the certificates and how
    // many, and records the answer on the case; asking again here would also
    // tell a family ordering their own that After orders for them. An absent
    // preference means the family was never asked, which is every case
    // predating that question.
    isVisible: (caseData) => !caseData?.dc_ordering_preference,
    component: CaseProgressConfirmDeathCertificates,
    isCompleted: (caseData, context) => {
      if (!caseData) return false;
      // Check if death certificates have been confirmed via appData
      if (
        context.appData.properties?.death_certificate_amount_confirmed === true
      ) {
        return true;
      }
      // Fallback to checking if death certificates have been ordered
      return caseData.date_death_certificates_ordered !== null;
    },
  },
  {
    slug: "sign_documents",
    name: "Sign Documents",
    hasUserAction: true,
    timeToComplete: "About 5 minutes to complete",
    component: CaseProgressSignDocuments,
    getContext: async (caseData, api) => {
      try {
        const caseDocuments: CaseDocumentsResponse = await api(
          `/cases/${caseData.record_id}/documents`,
          {
            params: {
              status: ["Complete", "Awaiting signatures"],
            },
          },
        );
        return {
          case_documents: caseDocuments.results || [],
        };
      } catch (error: any) {
        if (error?.statusCode === 404) {
          // 404 is expected when no documents exist yet
          return {
            case_documents: [],
          };
        }
        throw error; // Re-throw other errors
      }
    },
    getCompletedDate: (_caseData, _context) => {
      // Note: We don't currently have a completed_at field in the API response
      // This could be enhanced in the future if the API provides this information
      return undefined;
    },
    isCompleted: (caseData, context) => {
      const caseDocuments: CaseDocument[] = context?.case_documents || [];

      // Get all cremation authorization documents
      const cremationAuthDocs = caseDocuments.filter((doc: CaseDocument) =>
        doc.packet_slug?.startsWith("cremation_authorization"),
      );

      // If no cremation auth documents exist, stage is not complete
      if (cremationAuthDocs.length === 0) {
        return false;
      }

      // ALL cremation authorization documents must have status "complete"
      const allDocsComplete = cremationAuthDocs.every((doc: CaseDocument) => {
        const status = doc.status?.toLowerCase();
        return status === "complete";
      });

      return allDocsComplete;
    },
  },
  {
    slug: "obituary",
    name: "Obituary Writing",
    hasUserAction: true,
    timeToComplete: "A few hours to complete",
    component: CaseProgressObituary,
    getContext: async (caseData, api) => {
      let obituarySubmissions = { results: [] };

      try {
        obituarySubmissions = await api(
          `/cases/${caseData.record_id}/obituary/submissions`,
        );
      } catch (error: any) {
        if (error?.statusCode !== 404) {
          throw error; // Re-throw non-404 errors
        }
        // 404 is expected when no submissions exist yet
      }

      return {
        obituary_submissions: obituarySubmissions.results || [],
      };
    },
    getCompletedDate: (caseData, context) => {
      if (context?.obituary_submissions?.[0]?.created_at) {
        return new Date(context.obituary_submissions[0].created_at);
      }
      // If obituary was passed/skipped, we could return when that happened
      // but we don't have that date in the current context structure
      return undefined;
    },
    isCompleted: (caseData, context) => {
      // Check if obituary was passed/skipped via appData
      if (context.appData.properties?.obituary_passed === true) {
        return true;
      }

      // Check if obituary submissions exist
      if (!context?.obituary_submissions) {
        return false;
      }

      return context.obituary_submissions.length > 0;
    },
  },
  {
    slug: "death_certificates",
    name: "Death Certificate Service",
    hasUserAction: false,
    component: CaseProgressDeathCertificates,
    getCompletedDate: (caseData) => {
      return caseData?.date_death_certificates_ordered
        ? new Date(caseData.date_death_certificates_ordered)
        : undefined;
    },
    isCompleted: (caseData) => {
      if (!caseData) return false;
      if (!_.isEmpty(caseData?.date_death_certificates_ordered)) return true;
      return ["Cremation", "Delivery", "Complete"].includes(caseData?.stage);
    },
  },
  {
    slug: "review",
    name: "Review and Preparation",
    hasUserAction: false,
    component: CaseProgressReviewAndPreparation,
    getCompletedDate: (caseData) => {
      // Complete when case moved to Cremation, Delivery or Complete stage
      if (["Cremation", "Delivery", "Complete"].includes(caseData?.stage)) {
        return caseData?.date_entered_stage
          ? new Date(caseData.date_entered_stage)
          : undefined;
      }
      return undefined;
    },
    isCompleted: (caseData) => {
      if (!caseData) return false;
      return ["Cremation", "Delivery", "Complete"].includes(caseData?.stage);
    },
  },
  {
    slug: "cremation_completed",
    name: "Cremation",
    hasUserAction: false,
    component: CaseProgressCremationCompleted,
    getContext: async (caseData, api) => {
      try {
        const deliveryRecords = await api(
          `/cases/${caseData.record_id}/deliveries`,
        );
        return {
          delivery_records: deliveryRecords.results || [],
        };
      } catch (error: any) {
        if (error?.statusCode === 404) {
          // 404 is expected when no delivery records exist yet
          return {
            delivery_records: [],
          };
        }
        throw error; // Re-throw other errors
      }
    },
    isCompleted: (caseData) => {
      if (!caseData) return false;
      return ["Delivery", "Complete"].includes(caseData?.stage);
    },
  },
  {
    slug: "delivery_pending",
    name: "In Transit",
    hasUserAction: false,
    isVisible: (caseData) => {
      if (!caseData) return false;
      return caseData?.disposition_method !== "Scattered";
    },
    component: CaseProgressDelivery,
    getContext: async (caseData, api) => {
      try {
        const deliveryRecords = await api(
          `/cases/${caseData.record_id}/deliveries`,
        );
        return {
          delivery_records: deliveryRecords.results || [],
        };
      } catch (error: any) {
        if (error?.statusCode === 404) {
          // 404 is expected when no delivery records exist yet
          return {
            delivery_records: [],
          };
        }
        throw error; // Re-throw other errors
      }
    },
    isCompleted: (caseData, context) => {
      if (!caseData) return false;

      if (
        context?.delivery_records?.find(
          (record: any) => record.completed === true,
        )
      ) {
        return true;
      }

      return caseData?.stage === "Complete";
    },
  },
  {
    slug: "delivery_completed",
    name: "Delivery",
    hasUserAction: false,
    getCompletedDate: (caseData, context) => {
      // Find the first completed delivery record
      const completedDelivery = context?.delivery_records?.find(
        (record: any) => record.completed === true,
      );
      return completedDelivery?.completed_at
        ? new Date(completedDelivery.completed_at)
        : undefined;
    },
    isCompleted: (caseData) => {
      if (!caseData) return false;
      return caseData?.stage === "Complete";
    },
  },
];

/**
 * Filter stages by feature flags. Hides stages that should not yet be
 * visible in production. Keeps main-parity when a flag is off.
 */
function filterStagesByFeatureFlags(
  stages: CaseProgressStage[],
  flags: { arrangementFlowEnabled: boolean },
): CaseProgressStage[] {
  return stages.filter((stage) => {
    if (stage.slug === "arrangement" && !flags.arrangementFlowEnabled) {
      return false;
    }
    return true;
  });
}

function CaseProgress({
  caseData,
  careSpecialist,
  appData,
  arrangementFlowEnabled,
}: {
  caseData: any;
  careSpecialist?: TeamMember;
  appData: { properties?: Record<string, any> };
  arrangementFlowEnabled: boolean;
}) {
  const getIdToken = useGetIdToken();
  const authenticatedApi = useMemo(() => api({}, getIdToken), [getIdToken]);
  const [stageContexts, setStageContexts] = useState<Record<string, any>>({});
  const [isLoadingContexts, setIsLoadingContexts] = useState(true);
  const featureFilteredStages = useMemo(
    () =>
      filterStagesByFeatureFlags(CASE_PROGRESS_STAGES, {
        arrangementFlowEnabled,
      }),
    [arrangementFlowEnabled],
  );

  // Check if we have any loaded contexts (helps differentiate initial load from revalidation)
  const hasExistingContexts = Object.keys(stageContexts).length > 0;

  // Fetch contexts for stages that need them
  React.useEffect(() => {
    const fetchContexts = async () => {
      // Only show loading state if we don't have existing contexts (initial load)
      if (!hasExistingContexts) {
        setIsLoadingContexts(true);
      }

      const contexts: Record<string, any> = {};

      // Get all stages that need context
      const stagesWithContext = featureFilteredStages.filter(
        (stage) => stage.getContext && caseData,
      );

      // Fetch all contexts in parallel
      await Promise.all(
        stagesWithContext.map(async (stage) => {
          try {
            contexts[stage.slug] = await stage.getContext!(
              caseData,
              authenticatedApi,
            );
          } catch (error) {
            console.error(`Failed to fetch context for ${stage.slug}:`, error);
            contexts[stage.slug] = {};
          }
        }),
      );

      setStageContexts(contexts);
      setIsLoadingContexts(false);
    };

    if (caseData) {
      fetchContexts();
    }
  }, [caseData, authenticatedApi, hasExistingContexts, featureFilteredStages]);

  // Early return if no case data
  if (!caseData) {
    return null;
  }

  // Show skeleton loaders until all initial data is loaded
  if (isLoadingContexts) {
    // Filter visible stages to show correct skeleton items
    const visibleStages = featureFilteredStages.filter(
      (stage) => stage.isVisible === undefined || stage.isVisible?.(caseData),
    );

    const skeletonSteps = visibleStages.map((stage) => ({
      title: stage.name,
    }));

    return (
      <>
        <NextActionCardSkeleton />
        <div>
          <h3 className="mb-4 font-serif text-[21px] font-normal text-soil">
            Timeline
          </h3>
          <CaseProgressListSkeleton steps={skeletonSteps} />
        </div>
      </>
    );
  }

  // Filter visible stages
  const visibleStages = featureFilteredStages.filter(
    (stage) => stage.isVisible === undefined || stage.isVisible?.(caseData),
  );

  // Find the index of the first incomplete stage (this will be the "current" stage)
  const currentStageIndex = visibleStages.findIndex(
    (stage) =>
      !stage.isCompleted(caseData, {
        ...(stageContexts[stage.slug] || {}),
        appData,
      }),
  );

  // Get current stage and its context
  const currentStage =
    currentStageIndex >= 0 ? visibleStages[currentStageIndex] : null;
  const currentStageContext = currentStage
    ? {
        ...(stageContexts[currentStage.slug] || {}),
        appData,
      }
    : undefined;

  // Convert case stages to ProgressStep format
  const progressSteps: ProgressStep[] = visibleStages.map((stage, index) => {
    // Determine the state based on the stage's position relative to the current stage
    let state: "completed" | "current" | "upcoming";
    if (currentStageIndex === -1) {
      // All stages are completed
      state = "completed";
    } else if (index < currentStageIndex) {
      state = "completed";
    } else if (index === currentStageIndex) {
      state = "current";
    } else {
      state = "upcoming";
    }

    // Get the context for the stage and add appData
    const context = { ...(stageContexts[stage.slug] || {}), appData };

    // Get completed date for this stage if available
    const completedDate = stage.getCompletedDate?.(caseData, context);

    // Map the status for component rendering
    const componentStatus: "current" | "completed" | "upcoming" =
      state === "completed"
        ? "completed"
        : state === "current"
          ? "current"
          : "upcoming";

    // Create description content
    let description: ReactNode = null;

    if (state === "upcoming") {
      description = <p className="text-base text-soil"></p>;
    } else if (stage.component) {
      description = (
        <stage.component
          caseData={caseData}
          status={componentStatus}
          context={context}
          careSpecialist={careSpecialist}
        />
      );
    }

    return {
      title: stage.name,
      description:
        description || (stage.timeToComplete ? stage.timeToComplete : ""),
      state,
      completedDate,
    };
  });

  return (
    <>
      <NextActionCard
        caseData={caseData}
        currentStage={currentStage}
        currentStageContext={currentStageContext}
        careSpecialist={careSpecialist}
      />

      <div>
        <h3 className="mb-4 font-serif text-[21px] font-normal text-soil">
          Timeline
        </h3>
        <CaseProgressList steps={progressSteps} />
      </div>

      <KnowledgeBaseCallout
        subject={{ kind: "case", id: caseData.record_id }}
      />
    </>
  );
}

function CaseDetailsFrame({ children }: { children: ReactNode }) {
  return (
    <>
      <PortalContent>{children}</PortalContent>
      <ViewAsCustomerToggle />
    </>
  );
}

function FullCaseDetailsPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const { user } = useUser();
  const location = useLocation();
  const posthog = usePostHog();
  const isArrangementFlowEnabled = useFeatureFlagEnabled(
    "at-need-arrangement-flow",
  );
  const isCareSpecialist = useIsCareSpecialist();

  const swrConfig = {
    shouldRetryOnError: (error: any) => error?.statusCode !== 404,
  };

  // Fetch case data and app-data in parallel
  const {
    data: apiResponse,
    isLoading: isLoadingCase,
    error: caseError,
  } = useGET(`/cases/${caseId}`, undefined, swrConfig);
  const detailedCase = apiResponse as CaseDetails | undefined;
  const shouldShowCareSpecialist = hasReachedStage(detailedCase?.stage, "Care");
  const shouldFetchCareSpecialist =
    shouldShowCareSpecialist && !!detailedCase?.care_specialist;

  // Fetch app data in parallel with case data.
  //
  // No `fallbackData` here: the Hub decision below needs to distinguish
  // "not yet resolved" (undefined) from "resolved but empty" ({}). A fallback
  // would collapse both into {} and could show the wrong module state.
  const {
    data: appDataRaw,
    isLoading: isLoadingAppData,
    error: appDataError,
  } = useGET(caseId ? `/cases/${caseId}/app-data` : null, undefined, {
    shouldRetryOnError: (error: any) => error?.statusCode !== 404,
  });
  // Default for downstream consumers that only read nested properties.
  // Hub-level logic must use `appDataRaw` + `appDataError` instead so it can
  // distinguish unresolved / errored / empty.
  const appData = appDataRaw ?? EMPTY_APP_DATA;

  // Load care specialist data only once the case has reached Care.
  const {
    data: careSpecialistData,
    isLoading: isLoadingCareSpecialist,
    error: careSpecialistError,
  } = useGET(
    shouldFetchCareSpecialist
      ? `/team-members/${detailedCase.care_specialist}`
      : null,
    undefined,
    { shouldRetryOnError: false },
  );
  const careSpecialist = careSpecialistData as TeamMember | undefined;

  useEffect(() => {
    if (
      detailedCase &&
      (!shouldFetchCareSpecialist || careSpecialist) &&
      !isLoadingCase &&
      !isLoadingAppData
    ) {
      posthog?.setPersonProperties({
        latest_case_market_slug:
          detailedCase.market_slug?.toLowerCase() ?? null,
      });

      posthog?.capture("basecamp_case_details_page_viewed", {
        case_id: detailedCase.record_id,
        case_stage: detailedCase.stage,
        deceased_name: detailedCase.dcd_full_name,
        care_specialist_name: shouldShowCareSpecialist
          ? detailedCase.care_specialist_name
          : undefined,
      });
    }
  }, [
    detailedCase,
    shouldFetchCareSpecialist,
    shouldShowCareSpecialist,
    careSpecialist,
    isLoadingCase,
    isLoadingAppData,
    posthog,
  ]);

  const {
    allStartHubItemsDone,
    arrangementHubStatus,
    consultationHubStatus,
    hubEvaluationError,
    isHubDecisionPending,
    showStartHub,
    vitalsHubStatus,
  } = useStartHubState({
    appDataError,
    appDataRaw,
    caseId,
    caseStage: detailedCase?.stage,
    hasCase: !!detailedCase,
    isArrangementFlowEnabled,
    isCareSpecialist,
    locationState: location.state,
  });

  // Handle errors for case or care specialist
  if (caseError || hubEvaluationError) {
    const isNotFound = caseError?.statusCode === 404;
    const errorTitle = isNotFound
      ? "Arrangement Not Found"
      : "Error Loading Arrangement";
    const errorMessage = isNotFound
      ? "Sorry, we couldn't find the arrangement you're looking for."
      : "Sorry, there was a problem loading the arrangement details. Please try again later.";
    return (
      <CaseDetailsFrame>
        <div className="my-16 flex items-center justify-center px-3">
          <NotFound title={errorTitle} message={errorMessage} />
        </div>
      </CaseDetailsFrame>
    );
  }

  const userName = user?.first_name || "";
  const headerTitle = `We're here for you${userName ? `, ${userName}` : ""}`;

  // Hide Basecamp only when the arrangement flow is actually enabled and we
  // still need vitals data to decide whether this case should render the Hub
  // or the Timeline. When the flag is off (or undefined -> treated as off),
  // render Basecamp immediately to maintain strict parity with production.
  // Show loading state while any required data is being fetched
  if (isLoadingCase || isLoadingAppData || isHubDecisionPending) {
    return (
      <CaseDetailsFrame>
        <Loading message="Loading your case..." />
      </CaseDetailsFrame>
    );
  }

  // A case before Dispatch has no arrangement to offer yet: the landing
  // stands in for the timeline and the hub until it moves on.
  const leadLanding = detailedCase ? leadCaseLandingFor(detailedCase) : null;
  if (leadLanding) {
    return (
      <CaseDetailsFrame>
        <LeadCaseLanding model={leadLanding} caseId={caseId} />
      </CaseDetailsFrame>
    );
  }

  if (caseId && showStartHub) {
    return (
      <CaseDetailsFrame>
        <StartHub
          caseId={caseId}
          vitalsStatus={vitalsHubStatus}
          arrangementStatus={arrangementHubStatus}
          consultationStatus={consultationHubStatus}
          showTimelineLink={allStartHubItemsDone}
          timelineHref={`/cases/${caseId}`}
          timelineState={{ skipStartHub: true }}
        />
      </CaseDetailsFrame>
    );
  }

  return (
    <CaseDetailsFrame>
      <div className="flex flex-col gap-8">
        <PageHeader eyebrow="Arrangements" title={headerTitle} />
        <div className="flex flex-col gap-8">
          <div className="flex min-h-16 flex-col gap-2">
            <h2 className="text-lg font-semibold text-evergreen">
              {detailedCase?.dcd_full_name} is in our care.
            </h2>
            <h3 className="text-base text-oat-700">
              We'll guide you through each step along the way.
            </h3>
          </div>

          {/* Render CaseProgress when all required data is loaded */}
          {detailedCase && (
            <CaseProgress
              caseData={detailedCase}
              careSpecialist={careSpecialist}
              appData={appData}
              arrangementFlowEnabled={isArrangementFlowEnabled === true}
            />
          )}
        </div>

        {shouldShowCareSpecialist && (
          <CareSpecialistCard
            careSpecialist={careSpecialist}
            isLoading={isLoadingCase || isLoadingCareSpecialist}
            error={shouldFetchCareSpecialist ? careSpecialistError : undefined}
          />
        )}
      </div>
    </CaseDetailsFrame>
  );
}

function MinimalCaseView({
  name,
  stage,
  caseId,
}: MinimalCase & { caseId: string | undefined }) {
  const { home } = useMeHome();
  const quoteId = home ? quoteIdForCase(home, caseId) : null;
  return (
    <CaseDetailsFrame>
      <PageHeader title={name || "Loved One Name N/A"} />
      <p className="mt-4 text-base text-oat-800">
        {getStageDisplayDetails(stage).name}
      </p>
      {quoteId !== null && (
        <div className="mt-4">
          <AppButton href={quotePathById(quoteId)} variant="compact">
            View your quote
          </AppButton>
        </div>
      )}
    </CaseDetailsFrame>
  );
}

/** A contact who is neither Informant nor Purchaser sees the loved one's name
 * and the stage, never the case itself. The access is read before the case
 * is, so the full page's reads never run for them. */
export default function CaseDetailsPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const { user: firebaseUser } = useFirebase();
  const { data, isLoading } = useGET(
    firebaseUser && caseId ? "/me/cases" : null,
    undefined,
    { shouldRetryOnError: false },
  );
  if (isLoading) {
    return (
      <CaseDetailsFrame>
        <Loading message="Loading your case..." />
      </CaseDetailsFrame>
    );
  }
  const access = caseAccessFor(data, caseId ?? "");
  if (access.kind === "minimal") {
    return <MinimalCaseView {...access.case} caseId={caseId} />;
  }
  return <FullCaseDetailsPage />;
}
